import React, { useEffect, useMemo, useState } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Table,
    Spinner,
} from "reactstrap";
import { useMutation } from "@tanstack/react-query";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import { AvForm, AvField } from "availity-reactstrap-validation";
import Navigator from "src/components/Navigator";
import { toast } from "react-toastify";
import { routes, validRolesForAnalystAdmin } from "../../data";
import { createNewUser } from "../../api/apiFunctions";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom";
import axios from "axios";
import InputPhoneNumber from "src/components/Fields/InputPhoneNumber";
import UserFunctionsSelect from "src/modules/data-protection/pages/admin/administration/user-management/components/user-functions-select";
import { isDataProtectionModule } from "src/modules/data-protection/pages/admin/administration/user-management/components/user-functions-select/helpers";
import useUserFunctionAssignments from "src/modules/data-protection/pages/admin/administration/user-management/components/user-functions-select/hooks/useUserFunctionAssignments";

const AddUser = (props) => {
    const { modules, t, token } = props;
    const activeModule = modules?.active;
    const location = useLocation();

    const history = useHistory();
    const { assignAfterCreate } = useUserFunctionAssignments(t);

    const navigatorOptions = useMemo(() => {
        return {
            pageTitle: "New User",
            breadCrumbs: [
                {
                    title: "SIP",
                    link: "/",
                },
                {
                    title: "User Management",
                    link: routes[activeModule]?.listOfUsers,
                },
                {
                    title: "New User",
                    link: "#",
                },
            ],
            backButtonOptions: (defaults) => ({
                ...defaults,
                enable: true,
                onClick: () => history.push(routes[activeModule].listOfUsers),
            }),
            translate: true,
        };
    }, [activeModule, history]);

    const [items, setItems] = useState([
        {
            salutation: "",
            first_name: "",
            last_name: "",
            email: "",
            position: "",
            department: "",
            phone_number: "",
            mobile_number: "",
            role: "",
            functions: [],
        },
    ]);

    const handleSubmitMutation = useMutation({
        mutationFn: async () => {
            const payload = items.map((i) => {
                const item = {
                    gender: i.salutation,
                    first_name: i.first_name,
                    last_name: i.last_name,
                    email: i.email,
                    position: i.position,
                    role: i.role,
                    department: i.department,
                    phone_number: i.phone_number || "",
                    mobile_number: i.mobile_number || "",
                };
                return item;
            });
            const response = await createNewUser({
                token: token,
                payload,
                activeModule,
            });

            if (isDataProtectionModule(activeModule)) {
                await assignAfterCreate({
                    items,
                    createResponse: response,
                    token,
                });
            }
        },
        onSuccess: () => {
            toast("User created successfully.", {
                type: "success",
            });
            history.push(routes[activeModule].listOfUsers);
        },
        onError: (err) => {
            if (axios.isAxiosError(err)) {
                const error = err?.response?.data?.message;
                Object.values(error).forEach((val) => {
                    let result = "";

                    if (val === "user_email_already_exist") {
                        result = t("This email has already been used.");
                    } else if (val === "insufficient_license_ROLE_ANALYST_ADMIN") {
                        result = t("Insufficient licenses for role Analyst Admin.");
                    } else if (val === "insufficient_license_ROLE_ANALYST") {
                        result = t("Insufficient licenses for role Analyst.");
                    } else if (val === "insufficient_license_ROLE_EMPLOYEE") {
                        result = t("Insufficient licenses for role Employee.");
                    } else if (val === "insufficient_license_ROLE_TASK_OWNER") {
                        result = t("Insufficient licenses for role Task Owner.");
                    } else {
                        result = t(JSON.stringify(val));
                    }

                    toast(result, {
                        type: "error",
                    });
                });
            }
        },
    });

    const fieldValueChanged = (index, key, value) => {
        setItems((prevItems) =>
            prevItems.map((item, itemIndex) => {
                if (index === itemIndex) {
                    return {
                        ...item,
                        [key]: value,
                    };
                }
                return item;
            })
        );
    };

    const addUser = () => {
        items.push({
            salutation: "",
            email: "",
            position: "",
            first_name: "",
            last_name: "",
            department: "",
            phone_number: "",
            mobile_number: "",
            role: "",
            functions: [],
        });
    };

    const deleteUser = (index) => {
        if (items.length > 1) {
            setItems(
                items.filter((item, itemIndex) => {
                    return index !== itemIndex;
                })
            );
        }
    };

    useEffect(() => {
        const csvLoadedUsers = location.state?.csvLoadedUsers || [];
        if(csvLoadedUsers.length > 0) {
            const headersRow = csvLoadedUsers[0];
            const findHeaderIndex = (...names) => {
                return headersRow.findIndex((header) =>
                    names.includes(String(header || '').toLowerCase().trim())
                );
            };

            let salutationIndex = findHeaderIndex('salutation');
            let firstNameIndex = findHeaderIndex('first name', 'firstname');
            let lastNameIndex = findHeaderIndex('last name', 'lastname');
            let emailIndex = findHeaderIndex('email');
            let companyPositionIndex = findHeaderIndex('position', 'company position');
            let roleIndex = findHeaderIndex('role');
            let departmentIndex = findHeaderIndex('department', 'division');
            let phoneNumberIndex = findHeaderIndex('office number', 'phone number', 'phone');
            let mobileNumberIndex = findHeaderIndex('mobile number', 'mobile');

            // Fallback to previous fixed column order if headers are missing
            if (salutationIndex < 0) salutationIndex = 0;
            if (firstNameIndex < 0) firstNameIndex = 1;
            if (lastNameIndex < 0) lastNameIndex = 2;
            if (emailIndex < 0) emailIndex = 3;
            if (companyPositionIndex < 0) companyPositionIndex = 4;
            if (roleIndex < 0) roleIndex = 5;
            if (departmentIndex < 0) departmentIndex = 6;
            if (phoneNumberIndex < 0) phoneNumberIndex = 7;
            if (mobileNumberIndex < 0) mobileNumberIndex = 8;

            const rolesMapping = {
                "Analyst Admin": "ROLE_ANALYST_ADMIN",
                "Analyst": "ROLE_ANALYST",
                "Employee": "ROLE_EMPLOYEE",
                "Task Owner": "ROLE_TASK_OWNER",
            };
            
            const newRows = [];

            for (let i = 1; i < csvLoadedUsers.length; i++) {
                const items = csvLoadedUsers[i];
                if(items.length === headersRow.length){
                    const userRole = rolesMapping[items[roleIndex]] || "";
                    if (!userRole) {
                        toast(`${t('Invalid role')}: ${items[roleIndex]}`, {
                            type: "error"
                        });
                        continue;
                    }

                    newRows.push({
                        first_name: items[firstNameIndex] || "",
                        last_name: items[lastNameIndex] || "",
                        email: items[emailIndex] || "",
                        position: items[companyPositionIndex] || "",
                        role: userRole,
                        salutation: items[salutationIndex] || "",
                        department: items[departmentIndex] || "",
                        phone_number: items[phoneNumberIndex] || "",
                        mobile_number: items[mobileNumberIndex] || "",
                        functions: [],
                    });
                }
            }

            if (newRows.length > 0) {
                setItems(newRows);
            }
        }
    }, [
        location.state
    ]);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col lg="12" className="mb-4">
                            <Navigator {...navigatorOptions} />
                        </Col>
                        <Col xl="12">
                            <Card>
                                <CardBody>
                                    <AvForm
                                        className="needs-validation"
                                        onValidSubmit={handleSubmitMutation.mutate}
                                    >
                                        <Row>
                                            <Col sm="12">
                                                <div className="table-responsive">
                                                    <Table>
                                                        <thead>
                                                            <tr>
                                                                <th>{t("Salutation")}</th>
                                                                <th>
                                                                    {t("First name")}{" "}
                                                                    <span className="text-danger">*</span>
                                                                </th>
                                                                <th>{t("Last name")}</th>
                                                                <th>
                                                                    {t("Email")}{" "}
                                                                    <span className="text-danger">*</span>
                                                                </th>
                                                                <th>{`${t("Company Position")} ${t(
                                                                    "(Optional)"
                                                                )}`}</th>
                                                                <th>{`${t("Division")}`}</th>
                                                                <th>{`${t("Office Number")} ${t("(Optional)")}`}</th>
                                                                <th>{`${t("Mobile Number")} ${t("(Optional)")}`}</th>
                                                                <th>{t("Role")}</th>
                                                                {isDataProtectionModule(activeModule) && (
                                                                    <th>{t("Function")}</th>
                                                                )}
                                                                <th>{t("Delete")}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {items.map((user, index) => {
                                                                return (
                                                                    <tr>
                                                                        <td>
                                                                            <AvField
                                                                                type="select"
                                                                                name={`salutation[${index}]`}
                                                                                className="form-control"
                                                                                id={`salutation-${index}`}
                                                                                value={user.salutation || ""}
                                                                                onChange={(e) =>
                                                                                    fieldValueChanged(index, "salutation", e.target.value)
                                                                                }
                                                                                validate={{
                                                                                    required: {
                                                                                        value: true,
                                                                                        errorMessage: t("Please select a salutation"),
                                                                                    },
                                                                                }}
                                                                            >
                                                                                <option value="" hidden>
                                                                                    {t("Select Salutation")}
                                                                                </option>
                                                                                <option value="Mr.">{t("Mr.")}</option>
                                                                                <option value="Mrs.">{t("Mrs.")}</option>
                                                                                <option value="Ms.">{t("Ms.")}</option>
                                                                                <option value="Mx.">{t("Mx.")}</option>
                                                                            </AvField>
                                                                        </td>
                                                                        <td>
                                                                            <AvField
                                                                                name={`first_name[${index}]`}
                                                                                placeholder=""
                                                                                type="text"
                                                                                errorMessage={t(
                                                                                    "This field cannot be blank"
                                                                                )}
                                                                                className="form-control"
                                                                                validate={{
                                                                                    required: { value: true },
                                                                                }}
                                                                                id={`first-name-${index}`}
                                                                                value={user.first_name}
                                                                                onChange={(e) =>
                                                                                    fieldValueChanged(
                                                                                        index,
                                                                                        "first_name",
                                                                                        e.target.value
                                                                                    )
                                                                                }
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <AvField
                                                                                name={`last_name[${index}]`}
                                                                                placeholder=""
                                                                                type="text"
                                                                                className="form-control"
                                                                                validate={{
                                                                                    required: { value: false },
                                                                                }}
                                                                                id={`last-name-${index}`}
                                                                                value={user.last_name}
                                                                                onChange={(e) =>
                                                                                    fieldValueChanged(
                                                                                        index,
                                                                                        "last_name",
                                                                                        e.target.value
                                                                                    )
                                                                                }
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <AvField
                                                                                name={`email[${index}]`}
                                                                                placeholder=""
                                                                                type="text"
                                                                                errorMessage={t(
                                                                                    "This field cannot be blank"
                                                                                )}
                                                                                className="form-control"
                                                                                validate={{
                                                                                    required: { value: true },
                                                                                    email: true,
                                                                                }}
                                                                                id={`email-${index}`}
                                                                                value={user.email}
                                                                                onChange={(e) =>
                                                                                    fieldValueChanged(
                                                                                        index,
                                                                                        "email",
                                                                                        e.target.value
                                                                                    )
                                                                                }
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <AvField
                                                                                name={`position[${index}]`}
                                                                                placeholder=""
                                                                                type="text"
                                                                                className="form-control"
                                                                                id={`position-${index}`}
                                                                                value={user.position}
                                                                                onChange={(e) =>
                                                                                    fieldValueChanged(
                                                                                        index,
                                                                                        "position",
                                                                                        e.target.value
                                                                                    )
                                                                                }
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <AvField
                                                                                name={`department[${index}]`}
                                                                                placeholder=""
                                                                                type="text"
                                                                                className="form-control"
                                                                                id={`department-${index}`}
                                                                                value={user.department}
                                                                                validate={{
                                                                                    required: {
                                                                                        value: true
                                                                                    }
                                                                                }}
                                                                                onChange={(e) =>
                                                                                    fieldValueChanged(
                                                                                        index,
                                                                                        "department",
                                                                                        e.target.value
                                                                                    )
                                                                                }
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <div className="sip-phone-input">
                                                                                <InputPhoneNumber
                                                                                    id={`phone-number-${index}`}
                                                                                    name={`phone_number[${index}]`}
                                                                                    required={false}
                                                                                    style={{ width: "100%" }}
                                                                                    placeholder=""
                                                                                    errorMessage=""
                                                                                    value={user.phone_number || ""}
                                                                                    onChange={(phone) =>
                                                                                        fieldValueChanged(
                                                                                            index,
                                                                                            "phone_number",
                                                                                            phone
                                                                                        )
                                                                                    }
                                                                                />
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            <div className="sip-phone-input">
                                                                                <InputPhoneNumber
                                                                                    id={`mobile-number-${index}`}
                                                                                    name={`mobile_number[${index}]`}
                                                                                    required={false}
                                                                                    style={{ width: "100%" }}
                                                                                    placeholder=""
                                                                                    errorMessage=""
                                                                                    value={user.mobile_number || ""}
                                                                                    onChange={(phone) =>
                                                                                        fieldValueChanged(
                                                                                            index,
                                                                                            "mobile_number",
                                                                                            phone
                                                                                        )
                                                                                    }
                                                                                />
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            <AvField
                                                                                type="select"
                                                                                name={`role[${index}]`}
                                                                                className="form-control"
                                                                                id={`role-select[${index}]`}
                                                                                validate={{
                                                                                    required: {
                                                                                        value: true,
                                                                                        errorMessage: t(
                                                                                            "Please select a role"
                                                                                        ),
                                                                                    },
                                                                                }}
                                                                                value={user?.role || ""}
                                                                                onChange={(e) =>
                                                                                    fieldValueChanged(
                                                                                        index,
                                                                                        "role",
                                                                                        e.target.value
                                                                                    )
                                                                                }
                                                                            >
                                                                                <option value="" hidden>
                                                                                    {t("Select Role")}
                                                                                </option>
                                                                                {Object.entries(
                                                                                    validRolesForAnalystAdmin
                                                                                ).filter(([key, value]) =>
                                                                                    value.modules.includes(activeModule)
                                                                                ).map(([key, value]) => {
                                                                                    return (
                                                                                        <option value={key} key={key}>
                                                                                            {t(value.label)}
                                                                                        </option>
                                                                                    );
                                                                                })}
                                                                            </AvField>
                                                                        </td>
                                                                        {isDataProtectionModule(activeModule) && (
                                                                            <td>
                                                                                <UserFunctionsSelect
                                                                                    hideLabel
                                                                                    inputId={`functions-select[${index}]`}
                                                                                    value={user?.functions || []}
                                                                                    onChange={(functions) =>
                                                                                        fieldValueChanged(
                                                                                            index,
                                                                                            "functions",
                                                                                            functions
                                                                                        )
                                                                                    }
                                                                                />
                                                                            </td>
                                                                        )}
                                                                        <td>
                                                                            <Button
                                                                                type="button"
                                                                                color="link"
                                                                                className="waves-effect me-1 text-danger"
                                                                                onClick={() => deleteUser(index)}
                                                                            >
                                                                                <i className="ri-delete-bin-fill"></i>
                                                                            </Button>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col sm="12">
                                                <Button
                                                    color="secondary"
                                                    type="button"
                                                    onClick={addUser}
                                                >
                                                    {t("Add User")}
                                                </Button>
                                                <Button
                                                    color="primary"
                                                    type="submit"
                                                    style={{ float: "right" }}
                                                >
                                                    {handleSubmitMutation.isLoading ? (
                                                        <Spinner />
                                                    ) : (
                                                        t("Send Invite")
                                                    )}
                                                </Button>
                                            </Col>
                                        </Row>
                                    </AvForm>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

const mapStatetoProps = (state) => {
    const { token } = state.Login;
    const { Modules } = state;
    return { token, state, modules: Modules };
};

export default withNamespaces()(withRouter(connect(mapStatetoProps)(AddUser)));
